import { useState, useEffect, useCallback } from 'react';
import { DATABASE_SERVICE_URL } from '../services/workerUrls';
import { logService } from '../services/logService';
import { FIRST_USE_KEY } from '../components/chat/ChatInput';
import { PlayerStateManager } from '../utils/playerState';

// Helper for logging API calls
async function loggedFetch(
  method: string,
  endpoint: string,
  options?: RequestInit,
  table?: string
): Promise<Response> {
  const isDbOperation = table !== undefined;
  const logId = isDbOperation
    ? logService.db(method, table, {}, 'pending')
    : logService.api(method, endpoint, {}, 'pending');
  const startTime = Date.now();

  try {
    const response = await fetch(`${DATABASE_SERVICE_URL()}${endpoint}`, options);
    const duration = Date.now() - startTime;

    if (response.ok) {
      logService.updateLog(logId, { status: 'success', duration });
    } else {
      logService.updateLog(logId, { status: 'error', duration, message: `HTTP ${response.status}` });
    }
    return response;
  } catch (err) {
    const duration = Date.now() - startTime;
    logService.updateLog(logId, {
      status: 'error',
      duration,
      message: err instanceof Error ? err.message : 'Unknown error',
    });
    throw err;
  }
}

/**
 * Hook for managing player ID with localStorage persistence.
 *
 * Uses dynamically generated UUIDs for player IDs to support multiple players
 * and proper data isolation. IDs persist across sessions via localStorage.
 *
 * The reset function resets player-specific data (ship, credits, location, standings)
 * but does not affect the shared game world.
 */
export function usePlayerId() {
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Initialize player ID from localStorage or create new one
  const initializePlayer = useCallback(async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Get or generate player ID with UUID
        const currentPlayerId = PlayerStateManager.getPlayerId();

        // Check if player exists in database
        const response = await loggedFetch('GET', `/api/players/${currentPlayerId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            logService.info('Player', `Restored session for ${currentPlayerId}`);
            setPlayerId(currentPlayerId);
            setIsLoading(false);
            return;
          }
        }

        // Player not found - this should only happen on first load
        // The initialize-player endpoint (called by Reset Game) is responsible for creating players
        logService.error('Player', `Player ${currentPlayerId} not found in database`);
        throw new Error(
          `No player found in database. Please click "Reset Game" to initialize the game.`
        );
      } catch (err) {
        logService.error('Player', `Failed to initialize: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setError(err instanceof Error ? err.message : 'Failed to initialize player');
      } finally {
        setIsLoading(false);
      }
  }, []);

  // Run initialization on mount and on retry
  useEffect(() => {
    initializePlayer();
  }, [initializePlayer, retryCount]);

  // Retry function for error recovery
  const retry = useCallback(() => {
    setRetryCount(c => c + 1);
  }, []);

  // Reset player to initial state (keeps player ID, resets progress)
  const resetPlayer = useCallback(async () => {
    if (!playerId) return;

    setIsResetting(true);
    setError(null);

    const logId = logService.db('RESET', 'players', { playerId }, 'pending');
    const startTime = Date.now();

    try {
      const response = await fetch(`${DATABASE_SERVICE_URL()}/api/players/${playerId}/reset`, {
        method: 'POST',
      });

      const duration = Date.now() - startTime;

      if (!response.ok) {
        logService.updateLog(logId, { status: 'error', duration, message: `HTTP ${response.status}` });
        throw new Error('Failed to reset player');
      }

      logService.updateLog(logId, { status: 'success', duration });
      logService.info('Player', `Player ${playerId} reset successfully`);

      // Clear first-use flag to show "Let's play" prompt again
      localStorage.removeItem(FIRST_USE_KEY);
      // Force page reload to refresh all state
      window.location.reload();
    } catch (err) {
      logService.error('Player', `Reset failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setError(err instanceof Error ? err.message : 'Failed to reset player');
      setIsResetting(false);
    }
  }, [playerId]);

  return {
    playerId,
    isLoading,
    isResetting,
    error,
    resetPlayer,
    retry,
  };
}
